import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { inspectExtractionCell } from "@/features/data-curation/api/curation";
import {
  cellKey,
  MAX_ACTION_CELLS,
  MAX_RETRY_CELLS,
  type CellAction,
  type SelectedGridCell,
} from "@/features/data-curation/cellSelection";
import {
  cellStatus,
  claimStatus,
  hasNoTextLines,
} from "@/features/data-curation/cellStatus";
import {
  AddColumnBodyCell,
  AddColumnHeaderCell,
} from "@/features/data-curation/components/AddColumnCells";
import { ClearCellsDialog } from "@/features/data-curation/components/ClearCellsDialog";
import { DeleteColumnDialog } from "@/features/data-curation/components/DeleteColumnDialog";
import { DeleteRowDialog } from "@/features/data-curation/components/DeleteRowDialog";
import { EmptyGridCell } from "@/features/data-curation/components/EmptyGridCell";
import { EmptyGridRows } from "@/features/data-curation/components/EmptyGridRows";
import { ExtractionFigureImage } from "@/features/data-curation/components/ExtractionFigureImage";
import { GridLegend } from "@/features/data-curation/components/GridLegend";
import { LockedGridRows } from "@/features/data-curation/components/LockedGridRows";
import { OriginalSourceLink } from "@/features/data-curation/components/OriginalSourceLink";
import {
  isSpanCell,
  isSpanClaim,
  isSpanLocation,
  SourceSpan,
} from "@/features/data-curation/components/SourceSpan";
import {
  SearchNoteDialog,
  type SearchNoteTarget,
} from "@/features/data-curation/components/SearchNote";
import { SearchReport } from "@/features/data-curation/components/SearchReport";
import {
  rowPasses,
  rowStatusTitle,
  StatusBadge,
  StatusBar,
  StatusCorner,
  statusTitle,
  StatusUnderline,
} from "@/features/data-curation/components/StatusBadge";
import { figurePath, preloadFigures } from "@/features/data-curation/figureLoader";
import {
  CELL_BUTTON,
  NUMBER_CELL,
  ROW_CELL,
  VALUE_CELL,
} from "@/features/data-curation/gridCells";
import {
  MAX_MONITOR_ITEMS,
  monitorScope,
  monitorScopeLabel,
  monitorScopeTooLarge,
  type MonitorScope,
} from "@/features/data-curation/monitors";
import type {
  CurationColumn,
  CurationDatasetSummary,
  CurationVerificationStatus,
  ExtractionCell,
  ExtractionClaim,
  ExtractionCorpusEstimate,
  ExtractionFigureReference,
  ExtractionGridColumnStats,
  ExtractionRow,
} from "@/features/data-curation/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  Eraser,
  ExternalLink,
  FileSearch,
  FileText,
  Info,
  Maximize2,
  MessageSquare,
  Minimize2,
  Pencil,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

interface SelectedCell {
  rowId: string;
  columnId: string;
}

const formatValue = (value: unknown) => {
  if (value == null) return "Not extracted";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return "[Value cannot be displayed]";
  }
};

type IndexedFigureReference = ExtractionFigureReference & {
  figureIndex: number;
};

const figureReferences = (value: unknown): IndexedFigureReference[] => {
  const candidates = Array.isArray(value) ? value : [value];
  return candidates.flatMap((candidate, figureIndex) =>
    candidate != null &&
      typeof candidate === "object" &&
      typeof (candidate as { path?: unknown }).path === "string"
      ? [{ ...(candidate as ExtractionFigureReference), figureIndex }]
      : [],
  );
};

const safeJson = (value: Record<string, unknown>) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "{}";
  }
};

const citationHash = (lines: string | null | undefined) => {
  const references = (lines ?? "")
    .split(",")
    .map((reference) => {
      const match = reference.trim().match(/^L?(\d+)(?:\s*-\s*L?(\d+))?$/i);
      if (!match) return null;
      return match[2] ? `L${match[1]}-L${match[2]}` : `L${match[1]}`;
    })
    .filter((reference): reference is string => reference != null);
  return references.length > 0 ? `#${references.join(",")}` : "";
};

const safeExternalUrl = (value: string | null) => {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
};

const verifierConclusion = (claim: ExtractionClaim) => {
  const reason = claim.latest_verdict_payload.reason;
  return typeof reason === "string" && reason.trim() ? reason : null;
};

const primarySource = (row: ExtractionRow) =>
  row.sources.find((source) => source.is_primary) ?? row.sources[0] ?? null;

const sourceLabel = (row: ExtractionRow) => {
  const source = primarySource(row);
  return (
    source?.label ??
    source?.source_id ??
    source?.source_url ??
    row.source_paper_id ??
    row.row_key ??
    row.entity_key ??
    "Unlabelled row"
  );
};

const sourceCitationHref = (
  source: ExtractionRow["sources"][number],
): string | null => {
  if (!source.source_id) return null;
  const roots: Partial<
    Record<typeof source.source_type, string | undefined>
  > = {
    paper: "papers",
    trial: "trials",
    patent: "patents",
    other: source.source_id.startsWith("fda_") ? "fda" : undefined,
  };
  const root = roots[source.source_type];
  return root
    ? `/citations/${root}/${encodeURIComponent(source.source_id)}`
    : null;
};

/** The Paperclip page and the original page of one row source, side by side. */
const RowSourceLinks = ({ source }: { source: ExtractionRow["sources"][number] }) => {
  const citationHref = sourceCitationHref(source);
  return (
    <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      {citationHref && (
        <Link
          to={citationHref}
          className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
        >
          <FileText className="h-3 w-3" aria-hidden />
          Open in Paperclip
        </Link>
      )}
      <OriginalSourceLink
        docId={source.source_id}
        url={safeExternalUrl(source.source_url)}
        isPaper={source.source_type === "paper"}
      />
    </span>
  );
};

const countLabel = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

const cellSupportLabel = (cell: ExtractionCell) =>
  cell.support_type === "explanation"
    ? countLabel(cell.source_locations.length + (cell.source_url ? 1 : 0), "support")
    : countLabel(cell.claims.length, "source");

const rowStatus = (
  row: ExtractionRow,
  columns: CurationColumn[],
): CurationVerificationStatus => {
  const required = columns.filter((column) => column.required);
  const considered = required.length > 0
    ? required.map((column) =>
      row.cells.find((cell) => cell.column_id === column.column_id),
    )
    : row.cells;
  if (considered.some((cell) => cell && cellStatus(cell) === "rejected")) {
    return "rejected";
  }
  if (
    considered.length > 0 &&
    considered.every(
      (cell) =>
        cell != null &&
        cell.value != null &&
        ["verified", "matched", "explained"].includes(cellStatus(cell)),
    )
  ) {
    return considered.some(
      (cell) => cell && ["matched", "explained"].includes(cellStatus(cell)),
    )
      ? "explained"
      : "verified";
  }
  return "pending";
};

interface CurationTableProps {
  datasetId: string;
  repoId: string;
  columns: CurationColumn[];
  columnStats: ExtractionGridColumnStats[];
  rows: ExtractionRow[];
  summary: CurationDatasetSummary;
  filtered?: boolean;
  collecting?: boolean;
  collectionStartedAt?: string | null;
  collectionSummary?: string | null;
  /** Set when the curation can take no more rows and this page shows its last row. */
  rowLimit?: { limit: number; estimate?: ExtractionCorpusEstimate | null } | null;
  /** Each row gets a Delete button; set while the curation takes writes. */
  canDeleteRows?: boolean;
  /**
   * Set while the approved columns can change: a + button adds one, and the
   * column dialog edits or deletes one.
   */
  columnEdits?: {
    revision: number;
    /** All the columns, also the ones that a column filter hides. */
    columnCount: number;
    agentWorking: boolean;
    onAdd: () => void;
    onEdit: (column: CurationColumn) => void;
  } | null;
  onFocusColumn?: (columnId: string) => void;
  /** A click on a cell (its column) or on a row name (null): the details panel shows it. */
  onFocusCell?: (rowId: string, columnId: string | null) => void;
  /** Whether the selection bar offers the actions that go to the chat. */
  chatActions?: boolean;
  /** Clears the selected cells, or sends them to the chat to fill again or for a question. */
  onCellAction?: (action: CellAction, cells: SelectedGridCell[]) => void;
  /** Set while a Clear or Retry request runs. */
  cellActionPending?: boolean;
  /** The Monitor button of the selection; filters decide which columns and rows are whole. */
  monitor?: {
    rowsFiltered: boolean;
    columnsFiltered: boolean;
    onOpen: (scope: MonitorScope) => void;
  } | null;
  /** The grid filters, shown on the line of the legend. */
  filters?: ReactNode;
  /** Whether a grid filter is set; its count and Clear button take room on that line. */
  filtersActive?: boolean;
}

interface GridPoint {
  row: number;
  col: number;
}

interface EvidenceMenuState {
  x: number;
  y: number;
  label: string;
  open: () => void;
}

/** A one-command menu at the pointer. Right-click, or Control-click on a Mac, opens it. */
const EvidenceMenu = ({
  menu,
  onClose,
}: {
  menu: EvidenceMenuState;
  onClose: () => void;
}) =>
  createPortal(
    <div
      role="menu"
      aria-label={menu.label}
      data-evidence-menu
      className="fixed z-50 min-w-40 rounded-[3px] border border-zinc-200 bg-white p-1 shadow-md"
      style={{ left: menu.x, top: menu.y }}
    >
      <button
        type="button"
        role="menuitem"
        autoFocus
        className="font-ui flex w-full items-center gap-2 rounded-[3px] px-2 py-1.5 text-left text-sm text-zinc-800 hover:bg-zinc-100 focus-visible:bg-zinc-100 focus-visible:outline-none"
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.stopPropagation();
          onClose();
        }}
        onClick={() => {
          menu.open();
          onClose();
        }}
      >
        <FileSearch className="h-3.5 w-3.5" aria-hidden />
        {menu.label}
      </button>
    </div>,
    document.body,
  );

interface CellMonitorAction {
  /** The selection in words, such as `column “Phase”`; null for single cells. */
  label: string | null;
  tooLarge: boolean;
  onOpen: () => void;
}

/** Opens the details of the selection: a cell's evidence, a column's completeness, or a row's sources. */
interface OpenAction {
  label: string;
  title: string;
  run: () => void;
}

/** The actions on the selected cells; nothing while no cell is selected. The legend says how to select. */
const CellSelectionBar = (props: {
  cells: SelectedGridCell[];
  /** The selection in words, such as `rejected`; without it, the monitor's words. */
  label?: string | null;
  pending: boolean;
  opens: OpenAction[];
  onAction?: (action: CellAction) => void;
  /** Chat about cells and Retry, which hand the cells to the chat agent. */
  chatActions?: boolean;
  monitor?: CellMonitorAction | null;
  onClear: () => void;
}) =>
  props.cells.length > 0 ? <CellActions {...props} /> : null;

const BAR_BUTTON = "h-7 px-2";

/** The longest time from the first press of a double-click to its last event. */
const DOUBLE_CLICK_MS = 1_000;
/** How far, in pixels, the second click of a double-click can be from the first. */
const DOUBLE_CLICK_SLOP = 6;

const CellActions = ({
  cells,
  label,
  pending,
  opens,
  onAction,
  chatActions = true,
  monitor,
  onClear,
}: {
  cells: SelectedGridCell[];
  label?: string | null;
  pending: boolean;
  opens: OpenAction[];
  onAction?: (action: CellAction) => void;
  /** Chat about cells and Retry, which hand the cells to the chat agent. */
  chatActions?: boolean;
  monitor?: CellMonitorAction | null;
  onClear: () => void;
}) => {
  const [confirmClear, setConfirmClear] = useState(false);
  const filled = cells.filter((cell) => cell.revision != null).length;
  const tooManyToChat = cells.length > MAX_ACTION_CELLS;
  const tooManyToRetry = cells.length > MAX_RETRY_CELLS;
  const tooManyToClear = filled > MAX_ACTION_CELLS;
  const limits = onAction
    ? [
        tooManyToChat && `Chat about cells takes at most ${MAX_ACTION_CELLS} cells.`,
        tooManyToRetry &&
          `Retry takes at most ${MAX_RETRY_CELLS.toLocaleString("en-US")} cells.`,
        tooManyToClear && `Retry and Clear take at most ${MAX_ACTION_CELLS} cells with values.`,
      ]
        .filter(Boolean)
        .join(" ")
    : tooManyToChat
      ? `Select at most ${MAX_ACTION_CELLS} cells for an action.`
      : "";
  const selectionLabel = label ?? monitor?.label;
  const monitorButton = monitor && (
    <Button
      size="sm"
      variant="outline"
      className={BAR_BUTTON}
      disabled={monitor.tooLarge}
      title={
        monitor.tooLarge
          ? `Select at most ${MAX_MONITOR_ITEMS} rows and ${MAX_MONITOR_ITEMS} other cells for a monitor`
          : "Update these cells on a schedule"
      }
      onClick={monitor.onOpen}
    >
      <CalendarClock className="h-3.5 w-3.5" aria-hidden />
      Monitor
    </Button>
  );
  return (
    <div
      role="toolbar"
      aria-label="Selected cells"
      data-cell-toolbar
      className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 rounded-[3px] border border-amber-300 bg-amber-50 px-1 py-0.5"
    >
      <button
        type="button"
        onClick={onClear}
        aria-label="Deselect all cells"
        title="Deselect (Esc)"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] text-amber-900 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-700"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
      <span
        className="min-w-0 truncate text-sm font-medium tabular-nums text-amber-900"
        aria-live="polite"
      >
        {cells.length} {cells.length === 1 ? "cell" : "cells"} selected
        {selectionLabel && (
          <span className="font-normal"> ({selectionLabel})</span>
        )}
      </span>
      {limits && (
        <>
          <Tooltip content={limits}>
            <button
              type="button"
              aria-label="Selection limits"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] text-amber-900 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-700"
            >
              <Info className="h-4 w-4" aria-hidden />
            </button>
          </Tooltip>
          <span className="sr-only">{limits}</span>
        </>
      )}
      <span className="ml-2.5 flex shrink-0 flex-wrap items-center gap-1.5">
        {opens.map((open) => (
          <Button
            key={open.label}
            size="sm"
            variant="outline"
            className={BAR_BUTTON}
            title={open.title}
            onClick={open.run}
          >
            <FileSearch className="h-3.5 w-3.5" aria-hidden />
            {open.label}
          </Button>
        ))}
        {onAction && (
          <>
            {chatActions && (
              <Button
                size="sm"
                variant="outline"
                className={BAR_BUTTON}
                disabled={tooManyToChat}
                title="Put the cells in the chat box, then type your question"
                onClick={() => onAction("chat")}
              >
                <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                Chat about cells
              </Button>
            )}
            {chatActions && (
              <Button
                size="sm"
                variant="outline"
                className={BAR_BUTTON}
                disabled={tooManyToRetry || tooManyToClear || pending}
                title={
                  filled === 0
                    ? "Ask the agent to find the values of the cells"
                    : "Clear the cells now, then ask the agent to find their values again"
                }
                onClick={() => onAction("retry")}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                Retry
              </Button>
            )}
            {monitorButton}
            <Button
              size="sm"
              variant="outline"
              disabled={tooManyToClear || filled === 0 || pending}
              loading={pending}
              title={
                filled === 0
                  ? "The selected cells are already empty"
                  : "Clear the values of the cells now"
              }
              className={`${BAR_BUTTON} text-red-700 hover:bg-red-50 hover:text-red-800`}
              onClick={() => setConfirmClear(true)}
            >
              <Eraser className="h-3.5 w-3.5" aria-hidden />
              Clear
            </Button>
            <ClearCellsDialog
              open={confirmClear}
              count={filled}
              onCancel={() => setConfirmClear(false)}
              onConfirm={() => {
                setConfirmClear(false);
                onAction("clear");
              }}
            />
          </>
        )}
        {!onAction && monitorButton}
      </span>
    </div>
  );
};

/**
 * The labels of Legend and Expand show only when the line of the filters,
 * a container, has room for them; Expand goes first as the grid narrows.
 * Active filters add a count and a Clear filters button, so they need more room.
 */
const GRID_TOOL_LABELS = {
  idle: {
    legend: "sr-only @min-[41rem]:not-sr-only",
    expand: "sr-only @min-[46rem]:not-sr-only",
  },
  filtering: {
    legend: "sr-only @min-[55rem]:not-sr-only",
    expand: "sr-only @min-[60rem]:not-sr-only",
  },
};

/** About two lines of the row name column. */
const ROW_NAME_CHARS = 40;

/** A row name, cut at a word near ROW_NAME_CHARS with "…" until the row names are expanded. */
const RowName = ({ name, expanded }: { name: string; expanded: boolean }) => {
  const cut = name.slice(0, ROW_NAME_CHARS);
  const space = cut.lastIndexOf(" ");
  const short = !expanded && name.length > ROW_NAME_CHARS;
  return (
    <span
      className={`relative break-words font-medium leading-4 text-zinc-900 ${expanded ? "block" : "line-clamp-2"}`}
    >
      {short ? (
        <>
          <span aria-hidden>
            {`${(space > ROW_NAME_CHARS / 2 ? cut.slice(0, space) : cut).trimEnd()}…`}
          </span>
          <span className="sr-only">{name}</span>
        </>
      ) : (
        name
      )}
    </span>
  );
};

/** The kinds of cell that the counts above the grid select. */
type CountFilter = "pending" | "rejected" | "empty" | "not_searched";

const COUNT_FILTERS: {
  filter: CountFilter;
  /** The words after the count, and the words of its selection in the selection bar. */
  label: string;
  description: string;
  tone: string;
  pressedTone: string;
}[] = [
  {
    filter: "pending",
    label: "awaiting verification",
    description: "Values that the verifier has not checked yet.",
    tone: "bg-amber-50 text-amber-800 enabled:hover:bg-amber-100",
    pressedTone: "bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-400",
  },
  {
    filter: "rejected",
    label: "rejected",
    description: "Values that their cited text does not support.",
    tone: "bg-red-50 text-red-700 enabled:hover:bg-red-100",
    pressedTone: "bg-red-100 text-red-800 ring-1 ring-inset ring-red-400",
  },
  {
    filter: "empty",
    label: "empty",
    description: "Cells without a value.",
    tone: "bg-zinc-100 text-zinc-600 enabled:hover:bg-zinc-200 enabled:hover:text-zinc-800",
    pressedTone: "bg-zinc-200 text-zinc-800 ring-1 ring-inset ring-zinc-400",
  },
  {
    filter: "not_searched",
    label: "not searched yet",
    description: "Empty cells without a not-found note: nobody searched them yet.",
    tone: "bg-zinc-100 text-zinc-600 enabled:hover:bg-zinc-200 enabled:hover:text-zinc-800",
    pressedTone: "bg-zinc-200 text-zinc-800 ring-1 ring-inset ring-zinc-400",
  },
];

const filterCount = (summary: CurationDatasetSummary, filter: CountFilter) =>
  filter === "pending"
    ? summary.pending_cells
    : filter === "rejected"
      ? summary.rejected_cells
      : filter === "empty"
        ? summary.missing_cells
        : (summary.not_searched_cells ?? 0);

/** The keys of the shown cells of each count, by the rules that the server counts with. */
const countKeys = (rows: ExtractionRow[], columns: CurationColumn[]) => {
  const keys: Record<CountFilter, string[]> = {
    pending: [],
    rejected: [],
    empty: [],
    not_searched: [],
  };
  for (const row of rows) {
    const cells = new Map(row.cells.map((cell) => [cell.column_id, cell]));
    for (const column of columns) {
      const key = cellKey(row.id, column.column_id);
      const cell = cells.get(column.column_id);
      if (!cell || cell.value == null) {
        keys.empty.push(key);
        if (!row.search_notes?.[column.column_id]) keys.not_searched.push(key);
      } else if (
        cell.verification_status === "pending" ||
        cell.verification_status === "rejected"
      ) {
        keys[cell.verification_status].push(key);
      }
    }
  }
  return keys;
};

type HeaderHandlers = {
  onMouseDown: (event: ReactMouseEvent) => void;
  onClick: (event: ReactMouseEvent) => void;
  onKeyDown: (event: ReactKeyboardEvent) => void;
  onDoubleClick?: () => void;
  onContextMenu?: (event: ReactMouseEvent) => void;
};

/** The handlers of the grid rows. Their identity never changes, so rows can skip renders. */
interface GridRowActions {
  rowHeader: (row: ExtractionRow) => HeaderHandlers;
  pressCell: (event: ReactMouseEvent, point: GridPoint) => void;
  enterCell: (event: ReactMouseEvent, point: GridPoint) => void;
  cellKeyDown: (event: ReactKeyboardEvent, point: GridPoint) => void;
  showEvidenceMenu: (
    event: ReactMouseEvent,
    point: GridPoint,
    label: string,
    open: () => void,
  ) => void;
  openCell: (rowId: string, columnId: string) => void;
  openNote: (target: SearchNoteTarget) => void;
  openSources: (row: ExtractionRow) => void;
  deleteRow: (row: ExtractionRow) => void;
}

const IMAGE_TYPES = ["image", "figure"];

interface GridRowProps {
  row: ExtractionRow;
  rowIndex: number;
  columns: CurationColumn[];
  datasetId: string;
  /** The IDs of this row's selected columns, one per line; empty when none is selected. */
  selectedColumns: string;
  rowNamesExpanded: boolean;
  expandedColumnIds: Set<string>;
  canDeleteRows: boolean;
  addColumnCell: boolean;
  actions: GridRowActions;
}

/**
 * One row of the grid. A change of selection or a poll renders only the rows whose
 * props changed: a poll keeps the objects of unchanged rows.
 */
const GridRow = memo(function GridRow({
  row,
  rowIndex,
  columns,
  datasetId,
  selectedColumns,
  rowNamesExpanded,
  expandedColumnIds,
  canDeleteRows,
  addColumnCell,
  actions,
}: GridRowProps) {
  const selected = useMemo(
    () => new Set(selectedColumns ? selectedColumns.split("\n") : []),
    [selectedColumns],
  );
  const cells = new Map(row.cells.map((cell) => [cell.column_id, cell]));
  const derivedRowStatus = rowStatus(row, columns);
  const rowHeader = actions.rowHeader(row);
  return (
    <tr>
      <td
        className={`${NUMBER_CELL} z-10 p-0 text-right align-top text-2xs tabular-nums ${selected.size > 0
          ? "bg-amber-50 font-semibold text-amber-800"
          : "bg-white text-zinc-500 hover:bg-zinc-100"
          }`}
      >
        <button
          type="button"
          data-grid-header
          aria-label={`Select row ${row.row_number ?? row.row_description}`}
          title="Click to select the row. Double-click for details."
          {...rowHeader}
          className={`${CELL_BUTTON} block w-full px-1 py-1.5 text-right tabular-nums focus-visible:before:ring-zinc-900`}
        >
          {row.row_number}
        </button>
      </td>
      <th
        scope="row"
        title={rowStatusTitle[derivedRowStatus]}
        className={`${ROW_CELL} z-10 py-1.5 pl-3 pr-2 align-top font-normal ${rowPasses(derivedRowStatus) ? "bg-emerald-50" : "bg-white"
          }`}
      >
        <span className="flex items-start gap-1">
          <button
            type="button"
            tabIndex={-1}
            data-grid-header
            {...rowHeader}
            title={
              !rowNamesExpanded && row.row_description.length > ROW_NAME_CHARS
                ? row.row_description
                : undefined
            }
            className={`${CELL_BUTTON} min-w-0 flex-1 text-left`}
          >
            <RowName name={row.row_description} expanded={rowNamesExpanded} />
          </button>
          <button
            type="button"
            className="relative inline-flex shrink-0 items-center gap-0.5 rounded-[3px] px-0.5 text-2xs font-medium leading-4 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
            onClick={() => actions.openSources(row)}
            aria-label={`View ${countLabel(row.sources.length, "source")} for ${row.row_description}`}
            title={countLabel(row.sources.length, "source")}
          >
            <FileText className="h-3 w-3" aria-hidden />
            {row.sources.length}
          </button>
          {canDeleteRows && (
            <button
              type="button"
              className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] text-zinc-400 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700"
              onClick={() => actions.deleteRow(row)}
              aria-label={`Delete row ${row.row_number}: ${row.row_description}`}
              title="Delete row"
            >
              <Trash2 className="h-3 w-3" aria-hidden />
            </button>
          )}
        </span>
        <span className="sr-only">{rowStatusTitle[derivedRowStatus]}</span>
        <StatusBar status={derivedRowStatus} />
      </th>
      {columns.map((column, colIndex) => (
        <GridCell
          key={column.column_id}
          row={row}
          column={column}
          cell={cells.get(column.column_id)}
          rowIndex={rowIndex}
          colIndex={colIndex}
          isSelected={selected.has(column.column_id)}
          expanded={expandedColumnIds.has(column.column_id)}
          datasetId={datasetId}
          actions={actions}
        />
      ))}
      {addColumnCell && <AddColumnBodyCell />}
    </tr>
  );
});

interface GridCellProps {
  row: ExtractionRow;
  column: CurationColumn;
  cell: ExtractionCell | undefined;
  rowIndex: number;
  colIndex: number;
  isSelected: boolean;
  expanded: boolean;
  datasetId: string;
  actions: GridRowActions;
}

/** One cell of the grid. Selecting a column renders only the cells of that column. */
const GridCell = memo(function GridCell({
  row,
  column,
  cell,
  rowIndex,
  colIndex,
  isSelected,
  expanded,
  datasetId,
  actions,
}: GridCellProps) {
  const point = { row: rowIndex, col: colIndex };
  if (!cell || cell.value == null) {
    const note = row.search_notes?.[column.column_id];
    return (
      <EmptyGridCell
        column={column}
        rowLabel={sourceLabel(row)}
        note={note}
        selected={isSelected}
        onPress={(event) => actions.pressCell(event, point)}
        onEnter={(event) => actions.enterCell(event, point)}
        onKeyDown={(event) => actions.cellKeyDown(event, point)}
        onOpenNote={() => note && actions.openNote({ row, column, note })}
        onContextMenu={
          note
            ? (event) =>
              actions.showEvidenceMenu(event, point, "Details", () =>
                actions.openNote({ row, column, note }),
              )
            : undefined
        }
      />
    );
  }
  const derivedStatus = cellStatus(cell);
  const rejected = derivedStatus === "rejected";
  const openCell = () => actions.openCell(row.id, column.column_id);
  const figures = IMAGE_TYPES.includes(column.value_type)
    ? figureReferences(cell.value)
    : null;
  const tone = rejected
    ? "border-red-200 bg-red-50 hover:bg-red-100"
    : isSelected
      ? "border-zinc-200 bg-amber-50 hover:bg-amber-100"
      : "border-zinc-200 hover:bg-zinc-50";
  return (
    <td
      data-selected={isSelected ? "true" : undefined}
      className={`${VALUE_CELL} relative p-0 align-top ${tone} ${isSelected ? "outline outline-2 -outline-offset-2 outline-amber-400" : ""
        }`}
    >
      <button
        type="button"
        className={`${CELL_BUTTON} w-full px-2 py-1.5 text-left ${isSelected ? "focus-visible:before:ring-amber-500" : "focus-visible:before:ring-zinc-900"
          }`}
        aria-label={`Audit ${column.label}, ${sourceLabel(row)}; ${derivedStatus}; ${cellSupportLabel(cell)}${isSelected ? "; selected" : ""}`}
        title={`${statusTitle[derivedStatus]} · ${cellSupportLabel(cell)} · Right-click or double-click for details`}
        data-grid-cell
        onMouseDown={(event) => actions.pressCell(event, point)}
        onMouseEnter={(event) => actions.enterCell(event, point)}
        onKeyDown={(event) => actions.cellKeyDown(event, point)}
        onContextMenu={(event) => actions.showEvidenceMenu(event, point, "Details", openCell)}
        onDoubleClick={openCell}
        onClick={(event) => {
          if (event.detail === 0) openCell();
        }}
      >
        <span className="relative flex items-start gap-1.5">
          {figures ? (
            <span className="flex min-w-0 flex-1 gap-2 overflow-hidden">
              {figures.slice(0, 3).map((figure) => (
                <ExtractionFigureImage
                  key={figure.path}
                  datasetId={datasetId}
                  rowId={row.id}
                  columnId={column.column_id}
                  figureIndex={figure.figureIndex}
                  source={figure.path}
                  caption={figure.caption}
                  size="thumb"
                  className="h-12 w-16 shrink-0 rounded-[3px] border border-zinc-200 bg-white"
                />
              ))}
              {figures.length > 3 && (
                <span className="flex h-12 w-10 shrink-0 items-center justify-center rounded-[3px] border border-zinc-200 bg-zinc-50 text-2xs font-medium text-zinc-500">
                  +{figures.length - 3}
                </span>
              )}
            </span>
          ) : (
            <span
              className={`min-w-0 flex-1 break-words leading-4 text-zinc-800 ${expanded ? "whitespace-pre-wrap" : "line-clamp-2"
                }`}
            >
              {(cell.segments?.length ?? 0) > 1
                ? (cell.segments ?? []).map((segment) => (
                  <span key={segment.id ?? segment.position} className="inline">
                    {segment.separator_before}
                    <StatusUnderline status={segment.verification_status}>
                      {formatValue(segment.value)}
                    </StatusUnderline>
                  </span>
                ))
                : formatValue(cell.value)}
            </span>
          )}
        </span>
      </button>
      <StatusCorner status={derivedStatus} />
    </td>
  );
});

export const CurationTable = ({
  datasetId,
  repoId,
  columns,
  columnStats,
  rows,
  summary,
  filtered = false,
  collecting = false,
  collectionStartedAt = null,
  collectionSummary = null,
  rowLimit = null,
  canDeleteRows = false,
  columnEdits = null,
  onFocusColumn,
  onFocusCell,
  chatActions = true,
  onCellAction,
  cellActionPending = false,
  monitor = null,
  filters,
  filtersActive = false,
}: CurationTableProps) => {
  const toolLabels = GRID_TOOL_LABELS[filtersActive ? "filtering" : "idle"];
  const queryClient = useQueryClient();
  // Figure loads of this dataset stop when another dataset opens.
  const figureLoads = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    figureLoads.current = controller;
    return () => controller.abort();
  }, [datasetId]);
  const thumbnailPaths = useMemo(() => {
    const imageColumns = columns.filter((column) => IMAGE_TYPES.includes(column.value_type));
    if (imageColumns.length === 0) return [];
    return rows.flatMap((row) =>
      imageColumns.flatMap((column) => {
        const cell = row.cells.find((item) => item.column_id === column.column_id);
        if (!cell || cell.value == null) return [];
        return figureReferences(cell.value)
          .slice(0, 3)
          .map((figure) =>
            figurePath(
              datasetId,
              row.id,
              column.column_id,
              figure.figureIndex,
              "thumb",
              figure.path,
            ),
          );
      }),
    );
  }, [columns, datasetId, rows]);
  const thumbnailKey = thumbnailPaths.join("\n");
  // Load all thumbnails of the grid in row order, so they are ready before they scroll
  // into view; the ones on screen go first. A poll that adds rows queues only new ones.
  useEffect(() => {
    if (!thumbnailKey || !figureLoads.current) return;
    preloadFigures(thumbnailKey.split("\n"), figureLoads.current.signal);
  }, [thumbnailKey]);
  const [selected, setSelected] = useState<SelectedCell | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const [evidenceMenu, setEvidenceMenu] = useState<EvidenceMenuState | null>(null);
  const [dragging, setDragging] = useState(false);
  const anchorRef = useRef<GridPoint | null>(null);
  const dragStartRef = useRef<GridPoint | null>(null);
  useEffect(() => {
    const release = () => {
      dragStartRef.current = null;
      setDragging(false);
    };
    window.addEventListener("mouseup", release);
    return () => window.removeEventListener("mouseup", release);
  }, []);
  const rangeKeys = (from: GridPoint, to: GridPoint) => {
    const keys = new Set<string>();
    for (const row of rows.slice(Math.min(from.row, to.row), Math.max(from.row, to.row) + 1)) {
      for (const column of columns.slice(
        Math.min(from.col, to.col),
        Math.max(from.col, to.col) + 1,
      )) {
        keys.add(cellKey(row.id, column.column_id));
      }
    }
    return keys;
  };
  const toggleCell = (point: GridPoint) => {
    const key = cellKey(rows[point.row].id, columns[point.col].column_id);
    anchorRef.current = point;
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleKeys = (keys: string[]) => {
    anchorRef.current = null;
    setSelectedKeys((current) => {
      const next = new Set(current);
      const remove = keys.every((key) => next.has(key));
      for (const key of keys) {
        if (remove) next.delete(key);
        else next.add(key);
      }
      return next;
    });
  };
  const columnKeys = (columnId: string) => rows.map((row) => cellKey(row.id, columnId));
  const rowKeys = (rowId: string) => columns.map((column) => cellKey(rowId, column.column_id));
  const allKeys = () => rows.flatMap((row) => rowKeys(row.id));
  const showMenu = (event: ReactMouseEvent, label: string, open: () => void) => {
    event.preventDefault();
    const width = 220;
    const height = 40;
    setEvidenceMenu({
      x: Math.max(8, Math.min(event.clientX, window.innerWidth - width - 8)),
      y: Math.max(8, Math.min(event.clientY, window.innerHeight - height - 8)),
      label,
      open,
    });
  };
  /** Click selects one cell; Shift-, Ctrl-, or Cmd-click adds or removes one; a drag selects a range. */
  const showEvidenceMenu = (
    event: ReactMouseEvent,
    point: GridPoint,
    label: string,
    open: () => void,
  ) => {
    // On a Mac, Control-click is the right-click. That mousedown already toggled the cell.
    if (event.ctrlKey && !event.metaKey && event.button === 0) toggleCell(point);
    showMenu(event, label, open);
  };
  // The bar of a new selection shows above the grid and moves it down, so the second
  // click of a double-click can land on another element, even a button of the bar.
  // That click and its double-click go to what the first press chose instead.
  const firstPressRef = useRef<{
    open: (() => void) | null;
    time: number;
    x: number;
    y: number;
  } | null>(null);
  const notePress = (event: ReactMouseEvent, open: (() => void) | null) => {
    firstPressRef.current = {
      open,
      time: performance.now(),
      x: event.clientX,
      y: event.clientY,
    };
  };
  const isSecondClick = (event: { clientX: number; clientY: number }) => {
    const press = firstPressRef.current;
    return (
      press != null &&
      performance.now() - press.time <= DOUBLE_CLICK_MS &&
      Math.abs(event.clientX - press.x) <= DOUBLE_CLICK_SLOP &&
      Math.abs(event.clientY - press.y) <= DOUBLE_CLICK_SLOP
    );
  };
  const openAt = (point: GridPoint): (() => void) | null => {
    const row = rows[point.row];
    const column = columns[point.col];
    const cell = row.cells.find((item) => item.column_id === column.column_id);
    if (cell && cell.value != null) {
      return () => setSelected({ rowId: row.id, columnId: column.column_id });
    }
    const note = row.search_notes?.[column.column_id];
    return note ? () => setSearchNoteTarget({ row, column, note }) : null;
  };
  /**
   * Click on a header selects its whole column or row, or all cells; Shift-, Ctrl-, or
   * Cmd-click adds or removes them. Space does the same as Ctrl-click. Double-click and
   * the right-click menu open the details, and Enter too when `enterOpens` is set.
   */
  const headerHandlers = (
    keys: () => string[],
    details?: { label: string; open: () => void; enterOpens?: boolean },
  ): HeaderHandlers => ({
    onMouseDown: (event: ReactMouseEvent) => {
      // A double-click would select the words of the header.
      if (event.detail > 1) event.preventDefault();
    },
    onClick: (event: ReactMouseEvent) => {
      if (event.detail > 1) return;
      if (event.detail === 0 && details?.enterOpens) {
        details.open();
        return;
      }
      if (event.detail === 1) notePress(event, details?.open ?? null);
      if (event.shiftKey || event.metaKey || event.ctrlKey) {
        toggleKeys(keys());
        return;
      }
      anchorRef.current = null;
      setSelectedKeys(new Set(keys()));
    },
    onKeyDown: (event: ReactKeyboardEvent) => {
      if (event.key !== " ") return;
      event.preventDefault();
      toggleKeys(keys());
    },
    onDoubleClick: details ? () => details.open() : undefined,
    onContextMenu: details
      ? (event: ReactMouseEvent) => showMenu(event, details.label, details.open)
      : undefined,
  });
  useEffect(() => {
    if (!evidenceMenu) return;
    const dismiss = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest("[data-evidence-menu]")) return;
      setEvidenceMenu(null);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [evidenceMenu]);
  const pressCell = (event: ReactMouseEvent, point: GridPoint) => {
    if (event.button !== 0) return;
    // A double-click would select the words of the cell.
    if (event.detail > 1) event.preventDefault();
    // The first press already chose the cells; this press may be on another cell.
    if (event.detail > 1 && isSecondClick(event)) return;
    if (event.shiftKey || event.metaKey || event.ctrlKey) {
      event.preventDefault();
      toggleCell(point);
      return;
    }
    notePress(event, openAt(point));
    anchorRef.current = point;
    dragStartRef.current = point;
    const row = rows[point.row];
    const column = columns[point.col];
    setSelectedKeys(new Set([cellKey(row.id, column.column_id)]));
    prefetchEvidence(row, column);
    onFocusCell?.(row.id, column.column_id);
  };
  // A click often comes before a double-click or a right-click that opens the evidence.
  const prefetchEvidence = (row: ExtractionRow, column: CurationColumn) => {
    const cell = row.cells.find((item) => item.column_id === column.column_id);
    if (!cell || cell.value == null) return;
    void queryClient.prefetchQuery({
      queryKey: ["extraction-cell-detail", datasetId, row.id, column.column_id],
      queryFn: () => inspectExtractionCell(datasetId, row.id, column.column_id),
      staleTime: 30_000,
    });
    if (IMAGE_TYPES.includes(column.value_type) && figureLoads.current) {
      preloadFigures(
        figureReferences(cell.value).map((figure) =>
          figurePath(
            datasetId,
            row.id,
            column.column_id,
            figure.figureIndex,
            "full",
            figure.path,
          ),
        ),
        figureLoads.current.signal,
      );
    }
  };
  const enterCell = (event: ReactMouseEvent, point: GridPoint) => {
    const start = dragStartRef.current;
    if (!start || (event.buttons & 1) === 0) return;
    if (!dragging) {
      if (start.row === point.row && start.col === point.col) return;
      setDragging(true);
    }
    window.getSelection()?.removeAllRanges();
    setSelectedKeys(rangeKeys(start, point));
  };
  /** Space selects or unselects the focused cell; Enter opens it. */
  const cellKeyDown = (event: ReactKeyboardEvent, point: GridPoint) => {
    if (event.key !== " ") return;
    event.preventDefault();
    toggleCell(point);
  };
  const selectedCells = useMemo<SelectedGridCell[]>(() => {
    if (selectedKeys.size === 0) return [];
    return rows.flatMap((row) =>
      columns
        .filter((column) => selectedKeys.has(cellKey(row.id, column.column_id)))
        .map((column) => ({
          rowId: row.id,
          rowNumber: row.row_number,
          rowLabel: row.row_description,
          columnId: column.column_id,
          columnLabel: column.label,
          revision: (() => {
            const cell = row.cells.find((item) => item.column_id === column.column_id);
            return cell && cell.value != null ? cell.revision : null;
          })(),
        })),
    );
  }, [columns, rows, selectedKeys]);
  const selectedRowIds = new Set(selectedCells.map((cell) => cell.rowId));
  const selectedColumnIds = new Set(selectedCells.map((cell) => cell.columnId));
  const clearSelection = () => {
    setSelectedKeys(new Set());
    anchorRef.current = null;
  };
  // A count's selection is current while no other selection replaces its set.
  const [countSelection, setCountSelection] = useState<{
    filter: CountFilter;
    keys: Set<string>;
  } | null>(null);
  const shownCountKeys = useMemo(() => countKeys(rows, columns), [columns, rows]);
  const activeCountFilter =
    countSelection?.keys === selectedKeys ? countSelection.filter : null;
  const selectCount = (filter: CountFilter) => {
    if (activeCountFilter === filter) {
      clearSelection();
      return;
    }
    const keys = new Set(shownCountKeys[filter]);
    anchorRef.current = null;
    setSelectedKeys(keys);
    setCountSelection({ filter, keys });
  };
  const activeCount = COUNT_FILTERS.find((item) => item.filter === activeCountFilter);
  const countSelectionLabel = activeCount
    ? `${activeCount.label}${selectedCells.length < filterCount(summary, activeCount.filter) ? ", on this page" : ""}`
    : null;
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!countSelection) return;
    gridRef.current
      ?.querySelector('[data-selected="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [countSelection]);
  const hasSelection = selectedKeys.size > 0;
  useEffect(() => {
    if (!hasSelection) return;
    // Capture phase on pointerdown: a control that cancels pointerdown also
    // cancels mousedown, and a bubbling listener misses stopPropagation.
    const deselectOutside = (event: PointerEvent) => {
      if (event.button !== 0 || isSecondClick(event)) return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          "[data-grid-cell], [data-grid-header], [data-cell-toolbar], [data-count-filter], [role='dialog']",
        )
      ) {
        return;
      }
      setSelectedKeys(new Set());
      anchorRef.current = null;
    };
    const deselectOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const target = event.target;
      if (target instanceof Element && target.closest("[role='dialog']")) return;
      setSelectedKeys(new Set());
      anchorRef.current = null;
    };
    document.addEventListener("pointerdown", deselectOutside, true);
    document.addEventListener("keydown", deselectOnEscape);
    return () => {
      document.removeEventListener("pointerdown", deselectOutside, true);
      document.removeEventListener("keydown", deselectOnEscape);
    };
  }, [hasSelection]);
  const runCellAction = (action: CellAction) => {
    if (!onCellAction || selectedCells.length === 0) return;
    onCellAction(action, selectedCells);
    if (action !== "chat") clearSelection();
  };
  const selectionScope =
    monitor && selectedCells.length > 0
      ? monitorScope(selectedCells, {
          rowCount: rows.length,
          columnCount: columns.length,
          allRows: !monitor.rowsFiltered,
          allColumns: !monitor.columnsFiltered,
        })
      : null;
  const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null);
  const [selectedSourcesRow, setSelectedSourcesRow] =
    useState<ExtractionRow | null>(null);
  const [searchNoteTarget, setSearchNoteTarget] = useState<SearchNoteTarget | null>(null);
  const [rowToDelete, setRowToDelete] = useState<ExtractionRow | null>(null);
  const [columnToDelete, setColumnToDelete] = useState<CurationColumn | null>(null);
  // The edit dialog opens as the column dialog closes, so focus stays with it.
  const columnEditOpening = useRef(false);
  const [expandedColumnIds, setExpandedColumnIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [rowNamesExpanded, setRowNamesExpanded] = useState(false);
  // Each row gets the IDs of its selected columns, so a selection renders only its rows.
  const selectedColumnsByRow = useMemo(() => {
    const byRow = new Map<string, string[]>();
    for (const cell of selectedCells) {
      const ids = byRow.get(cell.rowId) ?? [];
      ids.push(cell.columnId);
      byRow.set(cell.rowId, ids);
    }
    return new Map([...byRow].map(([rowId, ids]) => [rowId, ids.join("\n")]));
  }, [selectedCells]);
  const currentRowActions: GridRowActions = {
    rowHeader: (row) => {
      const handlers = headerHandlers(() => rowKeys(row.id), {
        label: "Details",
        open: () => setSelectedSourcesRow(row),
      });
      return {
        ...handlers,
        onClick: (event: ReactMouseEvent) => {
          handlers.onClick(event);
          if (event.detail === 1 && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
            onFocusCell?.(row.id, null);
          }
        },
      };
    },
    pressCell,
    enterCell,
    cellKeyDown,
    showEvidenceMenu,
    openCell: (rowId, columnId) => setSelected({ rowId, columnId }),
    openNote: setSearchNoteTarget,
    openSources: setSelectedSourcesRow,
    deleteRow: setRowToDelete,
  };
  const latestRowActions = useRef(currentRowActions);
  latestRowActions.current = currentRowActions;
  const rowActions = useMemo<GridRowActions>(
    () => ({
      rowHeader: (row) => latestRowActions.current.rowHeader(row),
      pressCell: (event, point) => latestRowActions.current.pressCell(event, point),
      enterCell: (event, point) => latestRowActions.current.enterCell(event, point),
      cellKeyDown: (event, point) => latestRowActions.current.cellKeyDown(event, point),
      showEvidenceMenu: (event, point, label, open) =>
        latestRowActions.current.showEvidenceMenu(event, point, label, open),
      openCell: (rowId, columnId) => latestRowActions.current.openCell(rowId, columnId),
      openNote: (target) => latestRowActions.current.openNote(target),
      openSources: (row) => latestRowActions.current.openSources(row),
      deleteRow: (row) => latestRowActions.current.deleteRow(row),
    }),
    [],
  );
  const fieldCoverage =
    summary.total_cells > 0
      ? Math.round((summary.populated_cells / summary.total_cells) * 100)
      : 0;
  const allColumnsExpanded =
    columns.length > 0 &&
    rowNamesExpanded &&
    columns.every((column) => expandedColumnIds.has(column.column_id));
  const toggleColumnExpanded = (columnId: string) => {
    setExpandedColumnIds((current) => {
      const next = new Set(current);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      return next;
    });
  };
  const toggleAllColumnsExpanded = () => {
    setRowNamesExpanded(!allColumnsExpanded);
    setExpandedColumnIds(
      allColumnsExpanded
        ? new Set()
        : new Set(columns.map((column) => column.column_id)),
    );
  };
  const selectedColumnStats = useMemo(
    () =>
      columnStats.find(
        (item) => item.column.column_id === selectedColumnId,
      ) ?? null,
    [columnStats, selectedColumnId],
  );
  const detailQuery = useQuery({
    queryKey: [
      "extraction-cell-detail",
      datasetId,
      selected?.rowId,
      selected?.columnId,
    ],
    queryFn: () =>
      inspectExtractionCell(datasetId, selected!.rowId, selected!.columnId),
    enabled: selected != null,
  });
  const selectedData = useMemo(() => {
    if (!selected) return null;
    const row =
      detailQuery.data?.rows[0] ??
      rows.find((item) => item.id === selected.rowId);
    const column = columns.find((item) => item.column_id === selected.columnId);
    const cell = row?.cells.find((item) => item.column_id === selected.columnId);
    return row && column && cell && cell.value != null
      ? { row, column, cell }
      : null;
  }, [columns, detailQuery.data?.rows, rows, selected]);
  // Until the full cell loads, the dialog has the grid's copy, which has no source
  // wording and whose claims have no payloads and no verifier reasons.
  const evidencePending = selected != null && detailQuery.isPending;
  const wholeColumn =
    rows.length > 0 && selectedColumnIds.size === 1 && selectedCells.length === rows.length
      ? selectedCells[0]
      : null;
  const wholeRow =
    columns.length > 0 && selectedRowIds.size === 1 && selectedCells.length === columns.length
      ? (rows.find((row) => row.id === selectedCells[0].rowId) ?? null)
      : null;
  const detailViews = [
    ...(selectedCells.length === 1 && selectedCells[0].revision != null
      ? [
          {
            scope: "Cell",
            title: "See the evidence of the cell",
            run: () =>
              setSelected({
                rowId: selectedCells[0].rowId,
                columnId: selectedCells[0].columnId,
              }),
          },
        ]
      : []),
    ...(wholeColumn
      ? [
          {
            scope: "Column",
            title: `See how complete the column “${wholeColumn.columnLabel}” is`,
            run: () => setSelectedColumnId(wholeColumn.columnId),
          },
        ]
      : []),
    ...(wholeRow
      ? [
          {
            scope: "Row",
            title: "See the sources of the row",
            run: () => setSelectedSourcesRow(wholeRow),
          },
        ]
      : []),
  ];
  // A selection can be several things at once, such as one cell that is a whole row;
  // then each button names what it opens.
  const opens: OpenAction[] = detailViews.map(({ scope, title, run }) => ({
    label: detailViews.length > 1 ? `${scope} details` : "Details",
    title,
    run,
  }));

  return (
    <>
      <div
        className="space-y-4"
        onClickCapture={(event) => {
          if (event.detail > 1 && isSecondClick(event)) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onDoubleClickCapture={(event) => {
          if (!isSecondClick(event)) return;
          event.preventDefault();
          event.stopPropagation();
          const open = firstPressRef.current?.open;
          firstPressRef.current = null;
          open?.();
        }}
      >
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-zinc-600">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2" aria-live="polite">
              <span className="font-medium tabular-nums text-zinc-900">
                {summary.total_rows} {summary.total_rows === 1 ? "row" : "rows"}
              </span>
              <span aria-hidden className="text-zinc-300">·</span>
              <span className="font-medium tabular-nums text-zinc-900">
                {summary.populated_cells}/{summary.total_cells} fields populated (
                {fieldCoverage}%)
              </span>
              {COUNT_FILTERS.map(({ filter, label, description, tone, pressedTone }) => {
                const count = filterCount(summary, filter);
                if (count <= 0) return null;
                const shown = shownCountKeys[filter].length;
                const pressed = activeCountFilter === filter;
                const action =
                  shown === 0
                    ? "None of them are on this page."
                    : pressed
                      ? "Click to deselect them."
                      : shown < count
                        ? `Click to select the ${shown} on this page.`
                        : "Click to select them.";
                return (
                  <button
                    key={filter}
                    type="button"
                    data-count-filter
                    aria-pressed={pressed}
                    disabled={shown === 0}
                    title={`${description} ${action}`}
                    onClick={() => selectCount(filter)}
                    className={`rounded-full px-2 py-0.5 text-xs font-medium tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 disabled:cursor-default motion-safe:transition-colors ${pressed ? pressedTone : tone}`}
                  >
                    {count} {label}
                  </button>
                );
              })}
            </div>
            <div className="@container flex flex-1 basis-[34rem] flex-wrap items-center justify-end gap-2">
              {filters}
              {rows.length > 0 && <GridLegend labelClassName={toolLabels.legend} />}
              <Tooltip
                content={allColumnsExpanded ? "Collapse all cell values" : "Expand all cell values"}
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleAllColumnsExpanded}
                  aria-label={
                    allColumnsExpanded
                      ? "Collapse all cell values"
                      : "Expand all cell values"
                  }
                >
                  {allColumnsExpanded ? (
                    <Minimize2 className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <Maximize2 className="h-3.5 w-3.5" aria-hidden />
                  )}
                  <span className={toolLabels.expand}>
                    {allColumnsExpanded ? "Collapse" : "Expand"}
                  </span>
                </Button>
              </Tooltip>
            </div>
          </div>
          {rows.length > 0 && selectedCells.length > 0 && (
            <CellSelectionBar
              cells={selectedCells}
              label={countSelectionLabel}
              pending={cellActionPending}
              opens={opens}
              onAction={onCellAction ? runCellAction : undefined}
              chatActions={chatActions}
              monitor={
                monitor && selectionScope
                  ? {
                      label: monitorScopeLabel(selectionScope),
                      tooLarge: monitorScopeTooLarge(selectionScope),
                      onOpen: () => monitor.onOpen(selectionScope),
                    }
                  : null
              }
              onClear={clearSelection}
            />
          )}
        </div>

        <div
          ref={gridRef}
          className="max-h-[72vh] overflow-auto rounded-[3px] border border-zinc-200 bg-white [container-type:inline-size]"
        >
          <table
            className={`min-w-full border-separate border-spacing-0 text-left text-xs ${dragging ? "select-none" : ""
              }`}
          >
            <thead className="sticky top-0 z-30">
              <tr>
                <th
                  scope="col"
                  className={`${NUMBER_CELL} z-40 bg-zinc-50 p-0 text-right font-medium text-zinc-600`}
                >
                  <button
                    type="button"
                    data-grid-header
                    disabled={rows.length === 0}
                    aria-label="Select all cells"
                    title="Select all cells"
                    {...headerHandlers(allKeys)}
                    className={`${CELL_BUTTON} block w-full px-1 py-1.5 text-right hover:bg-zinc-100 focus-visible:before:ring-zinc-900 disabled:cursor-default disabled:hover:bg-transparent`}
                  >
                    <span aria-hidden className="relative">
                      #
                    </span>
                  </button>
                  <span className="sr-only">Row number</span>
                </th>
                <th
                  scope="col"
                  className={`${ROW_CELL} z-40 bg-zinc-50 p-0 font-medium text-zinc-600`}
                >
                  <div className="flex items-stretch">
                    <div className="min-w-0 flex-1 py-1.5 pl-3 pr-2">
                      <span className="block text-zinc-800">Row</span>
                      <span className="mt-0.5 block font-normal text-zinc-500">
                        Name and sources
                      </span>
                    </div>
                    <button
                      type="button"
                      aria-expanded={rowNamesExpanded}
                      aria-label={
                        rowNamesExpanded ? "Collapse all row names" : "Expand all row names"
                      }
                      title={rowNamesExpanded ? "Collapse row names" : "Expand row names"}
                      onClick={() => setRowNamesExpanded((expanded) => !expanded)}
                      className="border-l border-zinc-200 px-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900"
                    >
                      {rowNamesExpanded ? (
                        <Minimize2 className="h-3.5 w-3.5" aria-hidden />
                      ) : (
                        <Maximize2 className="h-3.5 w-3.5" aria-hidden />
                      )}
                    </button>
                  </div>
                </th>
                {columns.map((column) => (
                  <th
                    key={column.column_id}
                    scope="col"
                    className={`${VALUE_CELL} relative border-zinc-200 p-0 font-medium text-zinc-600 ${selectedColumnIds.has(column.column_id)
                      ? "bg-amber-50 [&_span:first-child]:font-semibold [&_span:first-child]:text-amber-800"
                      : "bg-zinc-50"
                      }`}
                  >
                    <div className="flex items-stretch">
                      <button
                        type="button"
                        data-grid-header
                        aria-label={`View completeness for ${column.label}`}
                        title={
                          columnEdits
                            ? "Click to select the column. Double-click for details, where you can also edit or delete it."
                            : "Click to select the column. Double-click for details."
                        }
                        {...headerHandlers(() => columnKeys(column.column_id), {
                          label: "Details",
                          open: () => setSelectedColumnId(column.column_id),
                          enterOpens: true,
                        })}
                        className={`${CELL_BUTTON} min-w-0 flex-1 px-2 py-1.5 text-left hover:bg-zinc-100 focus-visible:before:ring-zinc-900`}
                      >
                        <span className="relative block text-zinc-800 underline decoration-dotted decoration-zinc-400 underline-offset-4">
                          {column.label}
                        </span>
                        <span className="relative mt-0.5 block truncate font-normal text-zinc-500">
                          {column.value_type}
                          {column.required ? " · required" : " · optional"}
                        </span>
                      </button>
                      <button
                        type="button"
                        aria-label={`${expandedColumnIds.has(column.column_id)
                          ? "Collapse"
                          : "Expand"
                          } all values in ${column.label}`}
                        title={
                          expandedColumnIds.has(column.column_id)
                            ? "Collapse column values"
                            : "Expand column values"
                        }
                        onClick={() => toggleColumnExpanded(column.column_id)}
                        className="relative border-l border-zinc-200 px-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900"
                      >
                        {expandedColumnIds.has(column.column_id) ? (
                          <Minimize2 className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <Maximize2 className="h-3.5 w-3.5" aria-hidden />
                        )}
                      </button>
                    </div>
                  </th>
                ))}
                {columnEdits && <AddColumnHeaderCell onAdd={columnEdits.onAdd} />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <EmptyGridRows
                  columns={columns}
                  collecting={collecting}
                  filtered={filtered}
                  collectionSummary={collectionSummary}
                  collectionStartedAt={collectionStartedAt}
                  addColumnCell={columnEdits != null}
                />
              ) : (
                rows.map((row, rowIndex) => (
                  <GridRow
                    key={row.id}
                    row={row}
                    rowIndex={rowIndex}
                    columns={columns}
                    datasetId={datasetId}
                    selectedColumns={selectedColumnsByRow.get(row.id) ?? ""}
                    rowNamesExpanded={rowNamesExpanded}
                    expandedColumnIds={expandedColumnIds}
                    canDeleteRows={canDeleteRows}
                    addColumnCell={columnEdits != null}
                    actions={rowActions}
                  />
                ))
              )}
              {rows.length > 0 && rowLimit && (
                <LockedGridRows
                  columns={columns}
                  firstRowNumber={summary.total_rows + 1}
                  rowLimit={rowLimit.limit}
                  estimate={rowLimit.estimate}
                  addColumnCell={columnEdits != null}
                />
              )}
            </tbody>
          </table>
        </div>

      </div>

      <DeleteRowDialog
        datasetId={datasetId}
        row={rowToDelete}
        onClose={() => setRowToDelete(null)}
      />

      {columnEdits && (
        <DeleteColumnDialog
          datasetId={datasetId}
          revision={columnEdits.revision}
          columnCount={columnEdits.columnCount}
          agentWorking={columnEdits.agentWorking}
          column={columnToDelete}
          onClose={() => setColumnToDelete(null)}
        />
      )}

      <SearchNoteDialog
        target={searchNoteTarget}
        onClose={() => setSearchNoteTarget(null)}
      />

      <Dialog
        open={selectedSourcesRow != null}
        onOpenChange={(open) => !open && setSelectedSourcesRow(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Row sources</DialogTitle>
            <DialogDescription>
              {selectedSourcesRow?.row_description}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            The values of this row come from these papers and pages. A row has
            more than one source when more papers report the same row. To see the
            lines that support a value, open its cell. The search report below
            says, for each column, what was found and where a search found nothing.
          </p>
          {selectedSourcesRow && (
            <div className="max-h-[65vh] space-y-4 overflow-y-auto">
              <ol className="space-y-3">
                {selectedSourcesRow.sources.map((source, index) => {
                  const label =
                    source.label ??
                    source.source_id ??
                    source.source_url ??
                    `Source ${index + 1}`;
                  return (
                    <li
                      key={source.id}
                      className="rounded-[3px] border border-zinc-200 bg-zinc-50 p-3"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="break-all font-medium text-zinc-900">
                            {label}
                          </p>
                          {source.source_id && label !== source.source_id && (
                            <p className="mt-1 break-all font-mono text-xs text-zinc-500">
                              {source.source_id}
                            </p>
                          )}
                          <RowSourceLinks source={source} />
                        </div>
                        <div className="flex gap-1.5">
                          <Badge variant="default">{source.source_type}</Badge>
                          {source.is_primary && (
                            <Badge variant="blue">primary</Badge>
                          )}
                        </div>
                      </div>
                      {Object.keys(source.provenance).length > 0 && (
                        <details className="mt-2 text-xs text-zinc-500">
                          <summary className="cursor-pointer">
                            Technical provenance
                          </summary>
                          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-[3px] bg-white p-2">
                            {safeJson(source.provenance)}
                          </pre>
                        </details>
                      )}
                    </li>
                  );
                })}
              </ol>
              <SearchReport row={selectedSourcesRow} columns={columns} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedSourcesRow(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={selectedColumnStats != null}
        onOpenChange={(open) => !open && setSelectedColumnId(null)}
      >
        <DialogContent
          className="max-w-xl"
          onCloseAutoFocus={(event) => {
            if (columnToDelete || columnEditOpening.current) event.preventDefault();
            columnEditOpening.current = false;
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {selectedColumnStats?.column.label} completeness
            </DialogTitle>
            <DialogDescription>
              Coverage across all {summary.total_rows} rows, including rows
              outside the visible page.
            </DialogDescription>
          </DialogHeader>
          {selectedColumnStats && (
            <div className="space-y-4">
              <div className="rounded-[3px] border border-zinc-200 bg-zinc-50 p-4">
                <p className="text-2xl font-semibold tabular-nums text-zinc-900">
                  {selectedColumnStats.populated}/{summary.total_rows}
                </p>
                <p className="mt-1 text-sm text-zinc-600">fields populated</p>
              </div>
              <div className="text-xs leading-5 text-zinc-500">
                <p>
                  {selectedColumnStats.column.required ? "Required" : "Optional"}{" "}
                  · {selectedColumnStats.column.value_type}
                </p>
                {selectedColumnStats.column.description && (
                  <p className="mt-1">
                    {selectedColumnStats.column.description}
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedColumnStats && columnEdits && (
              <div className="flex flex-col-reverse gap-2 sm:mr-auto sm:flex-row">
                <Button
                  variant="outline"
                  className="text-red-700 hover:bg-red-50 hover:text-red-800"
                  onClick={() => {
                    setColumnToDelete(selectedColumnStats.column);
                    setSelectedColumnId(null);
                  }}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                  Delete column
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    columnEditOpening.current = true;
                    columnEdits.onEdit(selectedColumnStats.column);
                    setSelectedColumnId(null);
                  }}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                  Edit column
                </Button>
              </div>
            )}
            {selectedColumnStats && onFocusColumn && (
              <Button
                onClick={() => {
                  onFocusColumn(selectedColumnStats.column.column_id);
                  setSelectedColumnId(null);
                  window.setTimeout(
                    () =>
                      document.getElementById("curation-grid")?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      }),
                    0,
                  );
                }}
              >
                Show only this column
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setSelectedColumnId(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={selectedData != null}
        onOpenChange={(open) => !open && setSelected(null)}
      >
        <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="h-4 w-4" aria-hidden />
              {selectedData && (selectedData.cell.segments?.length ?? 0) > 1
                ? "Segmented evidence"
                : selectedData?.cell.support_type === "explanation"
                  ? "Explanation support"
                  : "Evidence and verification"}
            </DialogTitle>
            <DialogDescription>
              {selectedData?.column.label} ·{" "}
              {selectedData ? sourceLabel(selectedData.row) : ""}
            </DialogDescription>
          </DialogHeader>
          {selectedData && (
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1 text-sm">
              <div className="grid gap-4 rounded-[3px] border border-zinc-200 bg-zinc-50 p-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                    Extracted value
                  </p>
                  {["image", "figure"].includes(
                    selectedData.column.value_type,
                  ) ? (
                    <div className="mt-2 space-y-3">
                      {figureReferences(selectedData.cell.value).map(
                        (figure) => (
                          <figure
                            key={figure.path}
                            className="rounded-[3px] border border-zinc-200 bg-white p-2"
                          >
                            <ExtractionFigureImage
                              datasetId={datasetId}
                              rowId={selectedData.row.id}
                              columnId={selectedData.column.column_id}
                              figureIndex={figure.figureIndex}
                              source={figure.path}
                              caption={figure.caption}
                              className="max-h-[50vh] w-full rounded-[3px]"
                            />
                            {figure.caption && (
                              <figcaption className="mt-2 whitespace-pre-wrap text-xs leading-5 text-zinc-600">
                                {figure.caption}
                              </figcaption>
                            )}
                            <p className="mt-1 break-all font-mono text-2xs text-zinc-400">
                              {figure.path}
                            </p>
                          </figure>
                        ),
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap break-words text-zinc-900">
                      {formatValue(selectedData.cell.value)}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                    {selectedData.cell.support_type === "explanation"
                      ? "Explanation"
                      : "Source wording"}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-zinc-900">
                    {selectedData.cell.support_type === "explanation"
                      ? selectedData.cell.explanation
                      : evidencePending
                        ? "Loading…"
                        : selectedData.cell.as_reported}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                    Row sources
                  </p>
                  {selectedData.row.sources.length > 0 ? (
                    <ul className="mt-1 space-y-2">
                      {selectedData.row.sources.map((source) => (
                        <li key={source.id}>
                          <span className="break-all text-zinc-900">
                            {source.label ??
                              source.source_id ??
                              source.source_url ??
                              "Source"}
                          </span>
                          {source.is_primary && (
                            <span className="ml-1 text-xs text-zinc-400">
                              primary
                            </span>
                          )}
                          <RowSourceLinks source={source} />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 break-all text-zinc-900">
                      {selectedData.row.source_paper_id ?? "No source attached"}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                    {selectedData.cell.support_type === "explanation"
                      ? "Support"
                      : "Verification"}
                  </p>
                  <div className="mt-1">
                    <StatusBadge
                      status={cellStatus(selectedData.cell)}
                      sourceText={isSpanCell(selectedData.cell)}
                    />
                  </div>
                </div>
              </div>

              {(selectedData.cell.segments?.length ?? 0) > 1 ? (
                <section
                  className="space-y-3 rounded-[3px] border border-zinc-200 p-4"
                  aria-label="Cell segments"
                >
                  <div>
                    <h3 className="font-medium text-zinc-900">
                      Supported segments
                    </h3>
                    <p className="mt-1 text-xs text-zinc-500">
                      Each appended segment retains independent support and
                      verification.
                    </p>
                  </div>
                  <ol className="space-y-3">
                    {(selectedData.cell.segments ?? []).map((segment) => (
                      <li
                        key={segment.id ?? segment.position}
                        className="rounded-[3px] bg-zinc-50 p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="whitespace-pre-wrap leading-6 text-zinc-900">
                            {formatValue(segment.value)}
                          </p>
                          <StatusBadge
                            status={segment.verification_status}
                            sourceText={segment.source_locations.some(isSpanLocation)}
                          />
                        </div>
                        <p className="mt-2 text-xs leading-5 text-zinc-600">
                          {segment.support_type === "explanation"
                            ? segment.explanation
                            : segment.as_reported}
                        </p>
                        {segment.source_locations.length > 0 && (
                          <ul className="mt-2 space-y-1 text-xs text-zinc-500">
                            {segment.source_locations.map((location, index) => (
                              <li
                                key={`${location.path}:${location.lines}:${index}`}
                                className="break-all"
                              >
                                {location.span && !location.lines ? (
                                  <SourceSpan span={location.span} />
                                ) : (
                                  <>
                                    {location.path} · {location.lines}
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                        {safeExternalUrl(segment.source_url) && (
                          <a
                            href={safeExternalUrl(segment.source_url) ?? undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-800 underline decoration-blue-300 underline-offset-2"
                          >
                            Open source
                            <ExternalLink className="h-3 w-3" aria-hidden />
                          </a>
                        )}
                      </li>
                    ))}
                  </ol>
                </section>
              ) : selectedData.cell.support_type === "explanation" ? (
                <section
                  className="space-y-4 rounded-[3px] border border-blue-200 bg-blue-50/40 p-4"
                  aria-label="Explanation support"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    {selectedData.cell.record_match ? (
                      <div>
                        <h3 className="font-medium text-zinc-900">Matched the record</h3>
                        <p className="mt-1 text-xs text-zinc-600">
                          Copied from the field “{selectedData.cell.record_match.field}” of
                          the {selectedData.cell.record_match.source} record and checked
                          against it
                          {selectedData.cell.record_match.checked_at
                            ? ` on ${selectedData.cell.record_match.checked_at}`
                            : ""}
                          . Not checked against text lines.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <h3 className="font-medium text-zinc-900">Explanation</h3>
                        <p className="mt-1 text-xs text-zinc-600">
                          Explanation only — not automatically verified
                        </p>
                      </div>
                    )}
                    <StatusBadge
                      status={selectedData.cell.record_match ? "matched" : "explained"}
                    />
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-6 text-zinc-800">
                    {selectedData.cell.explanation}
                  </p>
                  {selectedData.cell.source_locations.length > 0 && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                        Supporting Paperclip lines
                      </p>
                      <ul className="mt-2 space-y-2">
                        {selectedData.cell.source_locations.map(
                          (location, locationIndex) => (
                            <li
                              key={`${location.path}:${location.lines}:${locationIndex}`}
                              className="grid gap-2 rounded-[3px] bg-white px-3 py-2 text-xs sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-4"
                            >
                              <span className="min-w-0">
                                <span className="block break-all font-mono text-zinc-700">
                                  {location.path}
                                </span>
                                <span className="mt-0.5 block text-zinc-500">
                                  Lines {location.lines}
                                </span>
                              </span>
                              <span className="flex flex-col items-start gap-1 sm:items-end">
                                <a
                                  href={`/citations/papers/${encodeURIComponent(selectedData.row.primary_source_id ?? selectedData.row.source_paper_id ?? "")}${citationHash(location.lines)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
                                >
                                  Open cited lines
                                  <ExternalLink className="h-3 w-3" aria-hidden />
                                </a>
                                <OriginalSourceLink
                                  docId={
                                    selectedData.row.primary_source_id ??
                                    selectedData.row.source_paper_id ??
                                    ""
                                  }
                                />
                              </span>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                  {safeExternalUrl(selectedData.cell.source_url) && (
                    <a
                      href={safeExternalUrl(selectedData.cell.source_url) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-blue-800 underline decoration-blue-300 underline-offset-2 hover:decoration-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-800"
                    >
                      Open source
                      <ExternalLink className="h-3 w-3" aria-hidden />
                    </a>
                  )}
                </section>
              ) : (
                <div className="space-y-3">
                  <h3 className="font-medium text-zinc-900">
                    Supporting evidence ({selectedData.cell.claims.length})
                  </h3>
                  {selectedData.cell.claims.length === 0 ? (
                    <p className="rounded-[3px] border border-amber-200 bg-amber-50 p-3 text-amber-800">
                      No supporting evidence is linked to this value.
                    </p>
                  ) : (
                    selectedData.cell.claims.map((claim, index) => (
                      <section
                        key={claim.id}
                        className="space-y-4 rounded-[3px] border border-zinc-200 p-4"
                        aria-label={`Evidence ${index + 1}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-zinc-900">
                              Evidence {index + 1}
                            </p>
                            <p className="mt-1 text-xs text-zinc-500">
                              {isSpanClaim(claim)
                                ? "Checked as the exact text of the passage below."
                                : hasNoTextLines(claim)
                                  ? "Not checked: the cited source has no text lines."
                                  : "Checked against the cited source."}
                            </p>
                          </div>
                          <StatusBadge
                            status={claimStatus(claim)}
                            sourceText={isSpanClaim(claim)}
                          />
                        </div>

                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Exact source
                          </p>
                          <ul className="mt-2 space-y-2">
                            {claim.source_locations.map((location, locationIndex) =>
                              location.span && !location.lines ? (
                              <li
                                key={`${location.path}:span:${locationIndex}`}
                                className="grid gap-2 rounded-[3px] bg-zinc-50 px-3 py-2 text-xs sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start sm:gap-4"
                              >
                                <SourceSpan span={location.span} />
                                <OriginalSourceLink
                                  docId={
                                    claim.source_id ??
                                    selectedData.row.primary_source_id ??
                                    selectedData.row.source_paper_id ??
                                    ""
                                  }
                                />
                              </li>
                              ) : (
                              <li
                                key={`${location.path}:${location.lines}:${locationIndex}`}
                                className="grid gap-2 rounded-[3px] bg-zinc-50 px-3 py-2 text-xs sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-4"
                              >
                                <span className="min-w-0">
                                  <span className="block break-all font-mono text-zinc-700">
                                    {location.path}
                                  </span>
                                  <span className="mt-0.5 block text-zinc-500">
                                    Lines {location.lines}
                                  </span>
                                </span>
                                <span className="flex flex-col items-start gap-1 sm:items-end">
                                  <a
                                    href={`/citations/papers/${encodeURIComponent(claim.source_id ?? selectedData.row.primary_source_id ?? selectedData.row.source_paper_id ?? "")}${citationHash(location.lines)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
                                  >
                                    Open cited lines
                                    <ExternalLink className="h-3 w-3" aria-hidden />
                                  </a>
                                  <OriginalSourceLink
                                    docId={
                                      claim.source_id ??
                                      selectedData.row.primary_source_id ??
                                      selectedData.row.source_paper_id ??
                                      ""
                                    }
                                  />
                                </span>
                              </li>
                              ),
                            )}
                          </ul>
                        </div>

                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Verifier conclusion
                          </p>
                          <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-zinc-800">
                            {evidencePending
                              ? "Loading…"
                              : (verifierConclusion(claim) ?? "No verifier conclusion yet.")}
                          </p>
                        </div>

                        <details className="rounded-[3px] border border-zinc-200 bg-zinc-50">
                          <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900">
                            Technical details
                          </summary>
                          <div className="space-y-3 border-t border-zinc-200 p-3">
                            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-xs">
                              <dt className="text-zinc-500">Claim key</dt>
                              <dd className="break-all font-mono text-zinc-800">
                                {claim.claim_key}
                              </dd>
                              <dt className="text-zinc-500">Claim revision</dt>
                              <dd className="tabular-nums text-zinc-800">
                                {claim.revision}
                              </dd>
                            </dl>
                            <div>
                              <p className="text-xs font-medium text-zinc-500">
                                Repository claim payload
                              </p>
                              <pre className="mt-1 max-h-52 overflow-auto whitespace-pre-wrap break-words rounded-[3px] bg-white p-3 text-xs text-zinc-700">
                                {evidencePending ? "Loading…" : safeJson(claim.payload)}
                              </pre>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-zinc-500">
                                Full verifier response
                              </p>
                              <pre className="mt-1 max-h-52 overflow-auto whitespace-pre-wrap break-words rounded-[3px] bg-white p-3 text-xs text-zinc-700">
                                {evidencePending
                                  ? "Loading…"
                                  : Object.keys(claim.latest_verdict_payload).length > 0
                                    ? safeJson(claim.latest_verdict_payload)
                                    : "No verifier result yet."}
                              </pre>
                            </div>
                          </div>
                        </details>
                      </section>
                    ))
                  )}
                </div>
              )}

              {detailQuery.data && detailQuery.data.cell_history.length > 0 && (
                <details className="rounded-[3px] border border-zinc-200">
                  <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-zinc-700">
                    Cell history ({detailQuery.data.cell_history.length})
                  </summary>
                  <ol className="space-y-3 border-t border-zinc-200 p-4">
                    {detailQuery.data.cell_history.map((event) => (
                      <li key={event.id} className="text-xs text-zinc-600">
                        <span className="font-medium text-zinc-800">
                          {event.action_type.replaceAll("_", " ")}
                        </span>
                        <span className="ml-2">
                          {new Date(event.created_at).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ol>
                </details>
              )}
            </div>
          )}
          <DialogFooter className="border-t border-zinc-100 pt-4">
            {selectedData?.cell.support_type !== "explanation" && (
              <Link
                to={`/repos/${encodeURIComponent(repoId)}`}
                className="mr-auto inline-flex h-8 items-center justify-center gap-2 rounded-[3px] border border-zinc-300 bg-white px-3.5 py-1 text-sm font-medium text-zinc-700 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
              >
                View evidence repository
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </Link>
            )}
            <Button variant="outline" onClick={() => setSelected(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {evidenceMenu && (
        <EvidenceMenu menu={evidenceMenu} onClose={() => setEvidenceMenu(null)} />
      )}
    </>
  );
};
